import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Input, Select, Spinner, Textarea } from '@cloudscape-design/components';
import { ShareButton } from '../../../components/ShareButton';
import { StageGraph } from '../../../components/stages/StageGraph';
import { ScenarioDetails, Difficulty, ScenarioUpdate } from '../../../common/types';
import { updateScenario } from '../../../common/api/scenarios';
import { StageView } from './stage';

export type ScenarioViewProps = {
  scenario: ScenarioDetails;
  refetch: () => void;
};

export function ScenarioView(props: ScenarioViewProps) {
  const { scenario, refetch } = props;
  const event = scenario.event;

  // Initialize state from the scenario. (Assuming `prompt` is stored on the scenario.)
  const [name, setName] = useState(scenario.name);
  const [description, setDescription] = useState(scenario.description);
  // const [prompt, setPrompt] = useState((scenario as any).prompt || '');
  const [difficulty, setDifficulty] = useState<Difficulty>(scenario.difficulty);
  const [roomCount, setRoomCount] = useState<number>(scenario.rooms.length);

  // Create a mutation for updating the scenario.
  const updateMutation = useMutation({
    mutationFn: ({ scenarioUuid, update }: { scenarioUuid: string; update: ScenarioUpdate }) =>
      updateScenario(scenarioUuid, update),
    onSuccess: () => {
      // Refresh the scenario data on success.
      refetch();
    },
  });

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      scenarioUuid: scenario.scenarioUuid,
      update: {
        name: name.trim(),
        description: description.trim(),
        // prompt: prompt.trim(),
        prompt: '',
        difficulty,
        roomCount,
      },
    });
  }, [scenario.scenarioUuid, name, description, prompt, difficulty, roomCount, updateMutation]);

  const loading = updateMutation.isPending;
  const changed =
    name !== scenario.name ||
    description !== scenario.description ||
    difficulty !== scenario.difficulty ||
    roomCount !== scenario.rooms.length;

  return (
    // <div key={scenario.scenarioUuid} className="bg-[#1e2a38] p-6 rounded-lg shadow-md max-w-4xl mx-auto">
    <div className="flex flex-col flex-grow min-h-0 p-4 lg:p-6 lg:max-w-6xl mx-auto">
      {/* Top Section with Image and Text */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Column: Image */}
        <div className="flex justify-center items-start">
          <div className="relative">
            <img
              src={scenario.image}
              alt={`${scenario.name} Initial Scene`}
              className="w-full max-w-[512px] max-h-[512px] lg:max-w-none lg:max-h-none rounded-lg object-cover"
            />
            {scenario.caption && (
              <div className="absolute bottom-0 left-0 bg-black bg-opacity-50 text-white text-sm px-2 py-1 rounded-tr-lg">
                {scenario.caption}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Name and Description */}
        <div className="flex flex-col justify-center space-y-4">
          {/* Editable Scenario Info */}
          <div className="mb-4 space-y-4">
            <div>
              <label className="block text-lg font-medium text-gray-200">Scenario Name</label>
              <Input
                name="name"
                disabled={loading}
                placeholder="Scenario name..."
                value={name}
                onChange={(e) => setName(e.detail.value)}
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-200">Description</label>
              <Textarea
                name="description"
                disabled={loading}
                placeholder="Description..."
                value={description}
                onChange={(e) => setDescription(e.detail.value)}
                rows={8}
              />
            </div>

            {/* <div>
                <label className="block text-sm font-medium text-gray-200">Detailed Prompt</label>
                <Textarea
                  name="prompt"
                  disabled={loading}
                  placeholder="Detailed prompt..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.detail.value)}
                />
              </div> */}
            <div>
              <label className="block text-lg font-medium text-gray-200">Difficulty</label>
              <Select
                name="difficulty"
                disabled={loading}
                options={[
                  { value: 'easy', label: 'Easy' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'hard', label: 'Hard' },
                ]}
                selectedOption={{
                  value: difficulty,
                  label: difficulty[0].toUpperCase() + difficulty.slice(1),
                }}
                onChange={(e) => setDifficulty(e.detail.selectedOption.value as Difficulty)}
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-200">Room Count</label>
              <Input
                name="roomCount"
                disabled={loading}
                placeholder="Room count..."
                value={roomCount.toString()}
                onChange={(e) => setRoomCount(parseInt(e.detail.value, 10))}
                type="number"
                inputMode="numeric"
              />
            </div>
            <button
              className={`text-black font-semibold text-sm py-2 px-4 rounded ${
                loading || !changed ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
              }`}
              disabled={loading || !changed}
              onClick={handleSave}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Spinner />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stage List */}
      {scenario.stages.length > 0 && (
        <div>
          <div className="mb-4">
            <h2 className="text-xl text-gray-200">Stages</h2>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4">
            {/* <StageGraph stages={scenario.stages} /> */}

            {scenario.stages.map((stage) => (
              <StageView key={stage.stageUuid} stage={stage} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
